import React from 'react'

import type { useBaseMetaTags } from '../useBaseMetaTags'

export type Meta = Parameters<ReturnType<typeof useBaseMetaTags>['getBaseMetaTags']>[0]['meta']

export const mockMetaData: Meta = {
  canonical: 'some-canonical',
  title: 'some-title',
  desc: 'description',
  fbAppId: '123456',
  dataLayer: [],
  taxonomyHierarchy: {
    vertical: {
      id: 1,
      name: 'name',
      slug: 'slug',
      seo_data: {
        page_title: 'page title',
        meta_description: 'meta desc',
      },
    },
  },
}

export const mockMetaWithSocialData: Meta = {
  canonical: 'some-canonical',
  title: 'some-title',
  desc: 'description',
  facebookDesc: 'Facebook desc',
  facebookImage: 'https://www.which.co.uk/facebook.jpg',
  facebookTitle: 'Facebook title',
  fbAppId: '123456',
  twitterDesc: 'Twitter desc',
  twitterImage: 'https://www.which.co.uk/twitter.jpg',
  twitterTitle: 'Twitter title',
  dataLayer: [],
  taxonomyHierarchy: {
    vertical: {
      id: 1,
      name: 'name',
      slug: 'slug',
      seo_data: {
        page_title: 'page title',
        meta_description: 'meta desc',
      },
    },
  },
}

export const baseExpectedMetaTags = [
  <meta key="twitter:site" content="@whichuk" name="twitter:site" />,
  <meta key="twitter:creator" content="@whichuk" name="twitter:creator" />,
  <meta key="og:type" content="website" property="og:type" />,
  <meta key="og:site_name" property="og:site_name" content="Which?" />,
  <meta key="og:locale" content="en_GB" property="og:locale" />,
  <meta key="language" content="en" name="language" />,
  <meta key="country" content="GB" name="country" />,
  <link key="favicon" href="/favicon.ico" rel="shortcut icon" type="image/ico" />,
]

export const canonicalUrlMetaTags = [
  <meta key="twitter:url" content="some-canonical" name="twitter:url" />,
  <meta key="og:url" content="some-canonical" property="og:url" />,
]

export const fallbackUrlMetaTags = [
  <meta key="twitter:url" content="test.com" name="twitter:url" />,
  <meta key="og:url" content="test.com" property="og:url" />,
]

export const robotsIndexMetaTag = (
  <meta key="robots" content="index, follow, max-image-preview:large" name="robots" />
)
export const robotsNoIndexMetaTag = (
  <meta key="robots" content="noindex, follow, max-image-preview:large" name="robots" />
)

export const expectedTwitterCardMetaTag = (
  <meta key="twitter:card" content="summary" name="twitter:card" />
)

export const expectedCanonicalMetaTag = (
  <link key="canonical" rel="canonical" href={mockMetaData.canonical} />
)

export const expectedTitleMetaTags = [
  <title key="title">{`${mockMetaData.title} - Which?`}</title>,
  <meta key="twitter:title" name="twitter:title" content={`${mockMetaData.title} - Which?`} />,
  <meta key="og:title" property="og:title" content={`${mockMetaData.title} - Which?`} />,
]

export const expectedImageMetaTags = [
  <meta key="twitter:image" name="twitter:image" content={'img-url'} />,
  <meta key="twitter:image:alt" name="twitter:image:alt" content={'image-alt'} />,
  <meta key="og:image" property="og:image" content={'img-url'} />,
]

export const expectedDescriptionMetaTags = [
  <meta key="description" content={mockMetaData.desc} name="description" />,
  <meta key="og:description" content={mockMetaData.desc} property="og:description" />,
  <meta key="twitter:description" content={mockMetaData.desc} name="twitter:description" />,
]

export const expectedFbAppIdTag = (
  <meta key="fb:app_id" content={mockMetaData.fbAppId} property="fb:app_id" />
)

export const expectedSocialMetaTags = [
  <title key="title">{`${mockMetaWithSocialData.title} - Which?`}</title>,
  <meta key="description" content={mockMetaWithSocialData.desc} name="description" />,
  <meta
    key="og:description"
    content={mockMetaWithSocialData.facebookDesc}
    property="og:description"
  />,
  <meta key="og:image" property="og:image" content={mockMetaWithSocialData.facebookImage} />,
  <meta
    key="og:title"
    property="og:title"
    content={`${mockMetaWithSocialData.facebookTitle} - Which?`}
  />,
  <meta
    key="twitter:description"
    content={mockMetaWithSocialData.twitterDesc}
    name="twitter:description"
  />,
  <meta key="twitter:image" name="twitter:image" content={mockMetaWithSocialData.twitterImage} />,
  <meta
    key="twitter:image:alt"
    name="twitter:image:alt"
    content={mockMetaWithSocialData.twitterTitle}
  />,
  <meta
    key="twitter:title"
    name="twitter:title"
    content={`${mockMetaWithSocialData.twitterTitle} - Which?`}
  />,
]

export const expectedNewsTitleMetaTags = [
  <title key="title">{`${mockMetaWithSocialData.title} - Which? News`}</title>,
  <meta
    key="og:title"
    property="og:title"
    content={`${mockMetaWithSocialData.facebookTitle} - Which? News`}
  />,
  <meta
    key="twitter:title"
    name="twitter:title"
    content={`${mockMetaWithSocialData.twitterTitle} - Which? News`}
  />,
]

export const expectedNewsMetaTag = (
  <link
    rel="alternate"
    href="https://www.which.co.uk/news/feed"
    type="application/rss+xml"
    title="Which? news RSS feed"
  />
)
